import React from 'react';
import { Box, Button, Link, Rating, styled, TextField, Typography } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { isDefinedAndNotNull } from '@pepperhq/types-sdk';
import StarBorderIcon from '@mui/icons-material/StarBorder';

import { isEmptyString } from 'lib/typeguards';
import { ContentLayout } from 'components/content/models/ContentLayout';

const StyledRating = styled(Rating)(() => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%'
}));

const StyledTextField = styled(TextField, {
    shouldForwardProp: (prop: string) => prop !== 'borderRadius' && prop !== 'primaryActionBackgroundColour'
})<{ borderRadius: string; primaryActionBackgroundColour: string }>(
    ({ borderRadius, primaryActionBackgroundColour }) => ({
        '& .MuiOutlinedInput-root': {
            borderRadius,
            '& .Mui-focused': {
                borderColor: primaryActionBackgroundColour
            }
        }
    })
);

const StyledPreviewCard = styled(Box, {
    shouldForwardProp: (prop: string) =>
        prop !== 'borderRadius' && prop !== 'backgroundColor' && prop !== 'getSecondaryCustomFontUrl'
})<{
    borderRadius?: string;
    backgroundColor?: string;
    getSecondaryCustomFontUrl?: string;
}>(({ theme, borderRadius, backgroundColor, getSecondaryCustomFontUrl }) => ({
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    alignItems: 'center',
    maxWidth: '400px',
    width: '100%',
    backgroundColor,
    ...(getSecondaryCustomFontUrl && {
        fontFamily: `'${secondaryFontFamily}', sans-serif`
    })
}));

const primaryFontFamily = 'ReviewsCustomFont';
const secondaryFontFamily = 'ReviewsSecondaryCustomFont';

interface ReviewsPreviewCardProps {
    activeLayout: ContentLayout;
    locationTitle: string;
    minimumRating: number;
    googleReviewRedirectUrl: string;
    appIconUrl: string;
    getCustomFontUrl: string;
    getSecondaryCustomFontUrl: string;
}

export const ReviewsPreviewCard = ({
    activeLayout,
    locationTitle,
    minimumRating,
    googleReviewRedirectUrl,
    appIconUrl,
    getCustomFontUrl,
    getSecondaryCustomFontUrl
}: ReviewsPreviewCardProps) => {
    const [rating, setRating] = React.useState<number | null>(null);
    const [comment, setComment] = React.useState('');
    const [showFeedback, setShowFeedback] = React.useState(false);

    React.useEffect(() => {
        if (!getCustomFontUrl) {
            return;
        }
        const style = document.createElement('style');
        style.textContent = `
            @font-face {
                font-family: '${primaryFontFamily}';
                src: url('${getCustomFontUrl}');
                font-display: swap;
            }
            @font-face {
                font-family: '${secondaryFontFamily}';
                src: url('${getSecondaryCustomFontUrl}');
                font-display: swap;
            }
        `;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, [getCustomFontUrl, getSecondaryCustomFontUrl]);

    const backgroundColor = React.useMemo(
        () => activeLayout?.anchor.properties?.anchorBackgroundColour,
        [activeLayout?.anchor.properties?.anchorBackgroundColour]
    );
    const titleTextColor = React.useMemo(
        () => activeLayout?.anchor.properties?.titleTextColour,
        [activeLayout?.anchor.properties?.titleTextColour]
    );
    const bodyTextColor = React.useMemo(
        () => activeLayout?.anchor.properties?.bodyTextColour,
        [activeLayout?.anchor.properties?.bodyTextColour]
    );
    const primaryActionTextColor = React.useMemo(
        () => activeLayout?.anchor.properties?.primaryActionTextColour,
        [activeLayout?.anchor.properties?.primaryActionTextColour]
    );

    const primaryActionBackgroundColour = React.useMemo(
        () => activeLayout?.anchor.properties?.primaryActionBackgroundColour,
        [activeLayout?.anchor.properties?.primaryActionBackgroundColour]
    );

    const borderRadius = React.useMemo(() => {
        const useRoundedCorners = activeLayout?.anchor.properties?.useRoundedCorners;
        const cornerRadius = activeLayout?.anchor.properties?.cornerRadius;
        if (useRoundedCorners) {
            return `${cornerRadius}px`;
        }
        return '0px';
    }, [activeLayout?.anchor.properties?.useRoundedCorners, activeLayout?.anchor.properties?.cornerRadius]);

    const handleReset = React.useCallback(() => {
        setRating(null);
        setShowFeedback(false);
        setComment('');
    }, []);

    const handleRatingChange = React.useCallback(
        (_: React.SyntheticEvent, newValue: number | null) => {
            const newRating = newValue ?? 0;
            if (newRating <= 0) {
                return;
            }
            setRating(newValue);
            if (
                isDefinedAndNotNull(googleReviewRedirectUrl) &&
                !isEmptyString(googleReviewRedirectUrl) &&
                isDefinedAndNotNull(minimumRating)
            ) {
                if (newRating >= minimumRating) {
                    window.open(googleReviewRedirectUrl, '_blank');
                    handleReset();
                } else {
                    setShowFeedback(true);
                }
            } else {
                setShowFeedback(true);
            }
        },
        [googleReviewRedirectUrl, minimumRating, handleReset]
    );

    const handleCommentChange = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setComment(e.target.value);
    }, []);

    const handleSubmit = React.useCallback(() => {
        if (rating < minimumRating) {
            return;
        }
        if (isDefinedAndNotNull(googleReviewRedirectUrl) && !isEmptyString(googleReviewRedirectUrl)) {
            window.open(googleReviewRedirectUrl, '_blank');
        }
    }, [rating, minimumRating, googleReviewRedirectUrl]);

    return (
        <Box display="flex" flexDirection="column" gap={2} alignItems="center">
            <StyledPreviewCard
                borderRadius={borderRadius}
                backgroundColor={backgroundColor}
                getSecondaryCustomFontUrl={getSecondaryCustomFontUrl}
            >
                <Box display="flex" flexDirection="column" alignItems="center" textAlign="center" gap={1}>
                    <Box component="img" src={appIconUrl} alt="Pepper" sx={{ width: 60, height: 60 }} />
                    <Box display="flex" flexDirection="column">
                        <Typography
                            variant="h6"
                            color={titleTextColor}
                            sx={
                                getCustomFontUrl
                                    ? { fontFamily: `'${primaryFontFamily}', sans-serif` }
                                    : undefined
                            }
                        >
                            <b>How Was Your Experience?</b>
                        </Typography>
                        <Typography variant="body2" color={bodyTextColor}>
                            {locationTitle} &bull; 1 hour ago
                        </Typography>
                    </Box>
                </Box>
                <StyledRating
                    value={rating}
                    onChange={handleRatingChange}
                    size="large"
                    icon={<StarIcon sx={{ fontSize: 48, color: '#faaf00' }} />}
                    emptyIcon={<StarBorderIcon sx={{ fontSize: 48, color: 'divider' }} />}
                />
                {showFeedback && (
                    <Box display="flex" flexDirection="column" gap={1} width="100%">
                        <StyledTextField
                            multiline
                            rows={4}
                            fullWidth
                            variant="outlined"
                            placeholder="Give us your feedback"
                            value={comment}
                            onChange={handleCommentChange}
                            borderRadius={borderRadius}
                            primaryActionBackgroundColour={primaryActionBackgroundColour}
                        />
                        <Button
                            fullWidth
                            variant="contained"
                            color="secondary"
                            size="large"
                            onClick={handleSubmit}
                            disabled={rating === null}
                            sx={{
                                backgroundColor: primaryActionBackgroundColour,
                                color: primaryActionTextColor,
                                borderRadius
                            }}
                            type="button"
                        >
                            Leave Feedback
                        </Button>
                    </Box>
                )}
            </StyledPreviewCard>
            <Link component="button" variant="body2" underline="always" onClick={handleReset} type="button">
                Reset preview
            </Link>
        </Box>
    );
};
